import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useSupplierUnansweredConversations } from "./useSupplierUnansweredConversations";
import { useSupplierActiveConversations } from "./useSupplierActiveConversations";

export interface NotificationItem {
  id: string;
  type: 'new_match' | 'new_message' | 'booking_confirmed' | 'new_conversation';
  customerName: string;
  categoryName: string;
  conversationId?: string;
  timestamp: string;
  unreadCount?: number;
}

export const useSupplierNotifications = (supplierId?: string) => {
  const { data: unansweredConversations = [] } = useSupplierUnansweredConversations(supplierId);
  const { data: activeConversations = [] } = useSupplierActiveConversations(supplierId);

  return useQuery({
    queryKey: ["supplier-notifications", supplierId, unansweredConversations.length, activeConversations.length],
    queryFn: async (): Promise<{
      notifications: NotificationItem[];
      totalCount: number;
      newMatchesCount: number;
      newMessagesCount: number;
      newConversationsCount: number;
      bookingConfirmedCount: number;
    }> => {
      if (!supplierId) {
        return {
          notifications: [],
          totalCount: 0,
          newMatchesCount: 0,
          newMessagesCount: 0,
          newConversationsCount: 0,
          bookingConfirmedCount: 0
        };
      }

      const notifications: NotificationItem[] = [];

      // 1. Get new matches (supplier_requests where accept === null)
      const { data: supplierPages } = await supabase
        .from("supplier_pages")
        .select("id")
        .eq('supplier_id', supplierId);
      
      if (supplierPages && supplierPages.length > 0) {
        const supplierPageIds = supplierPages.map(page => page.id);
        
        const { data: newRequests } = await supabase
          .from("supplier_requests")
          .select(`
            id,
            created_at,
            customer_requests!inner (
              id,
              first_name,
              last_name,
              supplier_categories!inner (
                name
              )
            )
          `)
          .in('supplier_page_id', supplierPageIds)
          .is('accept', null)
          .eq('archived', false)
          .order('created_at', { ascending: false });

        if (newRequests) {
          newRequests.forEach(req => {
            if (req.customer_requests) {
              notifications.push({
                id: req.id,
                type: 'new_match',
                customerName: `${req.customer_requests.first_name} ${req.customer_requests.last_name}`,
                categoryName: req.customer_requests.supplier_categories?.name || '',
                timestamp: req.created_at,
                conversationId: undefined
              });
            }
          });
        }
      }

      // 2. Get new messages from unanswered conversations
      const conversationIds = new Set<string>();

      unansweredConversations.forEach(conv => {
        if (conv.unread_count > 0 && conv.customer_requests) {
          conversationIds.add(conv.id);
          notifications.push({
            id: conv.id,
            type: 'new_message',
            customerName: `${conv.customer_requests.first_name} ${conv.customer_requests.last_name}`,
            categoryName: conv.customer_requests.supplier_categories?.name || '',
            conversationId: conv.id,
            timestamp: conv.last_activity_at,
            unreadCount: conv.unread_count
          });
        }
      });

      // 3. Get new messages from active conversations (skip if already counted)
      activeConversations.forEach(conv => {
        if (conv.unread_count > 0 && conv.customer_requests && !conversationIds.has(conv.id)) {
          conversationIds.add(conv.id);
          notifications.push({
            id: conv.id,
            type: 'new_message',
            customerName: `${conv.customer_requests.first_name} ${conv.customer_requests.last_name}`,
            categoryName: conv.customer_requests.supplier_categories?.name || '',
            conversationId: conv.id,
            timestamp: conv.last_activity_at,
            unreadCount: conv.unread_count
          });
        }
      });

      // 4. Get confirmed bookings (where customer confirmed)
      const { data: confirmedBookings } = await supabase
        .from("conversations")
        .select(`
          id,
          last_activity_at,
          customer_requests!inner (
            first_name,
            last_name,
            supplier_categories!inner (
              name
            )
          ),
          supplier_requests!inner (
            supplier_pages!inner (
              supplier_id
            )
          )
        `)
        .eq("supplier_requests.supplier_pages.supplier_id", supplierId)
        .eq("confirm_booking_customer", true)
        .order('last_activity_at', { ascending: false })
        .limit(10);

      if (confirmedBookings) {
        confirmedBookings.forEach(conv => {
          if (conv.customer_requests) {
            notifications.push({
              id: conv.id,
              type: 'booking_confirmed',
              customerName: `${conv.customer_requests.first_name} ${conv.customer_requests.last_name}`,
              categoryName: conv.customer_requests.supplier_categories?.name || '',
              conversationId: conv.id,
              timestamp: conv.last_activity_at
            });
          }
        });
      }

      // 5. Get new conversations (supplier hasn't read yet)
      if (supplierPages && supplierPages.length > 0) {
        const supplierPageIds = supplierPages.map(page => page.id);

        const { data: newConversations } = await supabase
          .from("conversations")
          .select(`
            id,
            last_activity_at,
            customer_requests!inner (
              first_name,
              last_name,
              supplier_categories!inner (
                name
              )
            ),
            supplier_requests!inner (
              supplier_page_id,
              accept
            )
          `)
          .in("supplier_requests.supplier_page_id", supplierPageIds)
          .eq("supplier_requests.accept", true)
          .is("supplier_last_read_at", null)
          .order('last_activity_at', { ascending: false });

        if (newConversations) {
          newConversations.forEach(conv => {
            if (conv.customer_requests && !conversationIds.has(conv.id)) {
              conversationIds.add(conv.id);
              notifications.push({
                id: `new-conv-${conv.id}`,
                type: 'new_conversation',
                customerName: `${conv.customer_requests.first_name} ${conv.customer_requests.last_name}`,
                categoryName: conv.customer_requests.supplier_categories?.name || '',
                conversationId: conv.id,
                timestamp: conv.last_activity_at
              });
            }
          });
        }
      }

      // Sort all notifications by timestamp (newest first)
      notifications.sort((a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );

      // Calculate counts
      const newMatchesCount = notifications.filter(n => n.type === 'new_match').length;
      const newMessagesCount = notifications.filter(n => n.type === 'new_message').length;
      const newConversationsCount = notifications.filter(n => n.type === 'new_conversation').length;
      const bookingConfirmedCount = notifications.filter(n => n.type === 'booking_confirmed').length;
      const totalCount = newMatchesCount + newMessagesCount + newConversationsCount + bookingConfirmedCount;

      return {
        notifications,
        totalCount,
        newMatchesCount,
        newMessagesCount,
        newConversationsCount,
        bookingConfirmedCount
      };
    },
    enabled: !!supplierId,
    refetchInterval: 30000,
  });
};
